import React, { useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
  Paper,
  FormControlLabel,
  Switch,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { getMetadatosWhereIdTipoContenidos } from "../helpers/MetadatoData";

const tiposContenido =
  JSON.parse(localStorage.getItem("siam-tipo_contenidos"))?.tipo_contenidos || [];

const FIXED_COLS = ["id_caja", "lote"];
const CAJA_COLS  = ["id_tipo_caja", "id_sector"];

const TIPO_DATO_COLOR = {
  string:  { bg: "#E3F2FD", text: "#1565C0" },
  number:  { bg: "#E8F5E9", text: "#2E7D32" },
  integer: { bg: "#E8F5E9", text: "#2E7D32" },
  date:    { bg: "#FFF3E0", text: "#E65100" },
  boolean: { bg: "#F3E5F5", text: "#6A1B9A" },
};
const tipoBadge = (tipo) => TIPO_DATO_COLOR[tipo?.toLowerCase()] ?? { bg: "#F5F5F5", text: "#616161" };

export const ExportadorCSVTipoContenidos = () => {
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [selectedMetadatos, setSelectedMetadatos] = useState([]);
  const [metadatosFiltrados, setMetadatosFiltrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incluirCamposCaja, setIncluirCamposCaja] = useState(false);

  const fetchMetadatos = async (tipoId) => {
    setLoading(true);
    try {
      const data = await getMetadatosWhereIdTipoContenidos(tipoId);
      setMetadatosFiltrados(data || []);
      setSelectedMetadatos((data || []).map((m) => m.id)); // seleccionar todos por defecto
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) =>
    setSelectedMetadatos((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const allSelected = metadatosFiltrados.length > 0 && selectedMetadatos.length === metadatosFiltrados.length;

  const toggleAll = () =>
    setSelectedMetadatos(allSelected ? [] : metadatosFiltrados.map((m) => m.id));

  const handleTipoChange = (_, value) => {
    setSelectedTipo(value);
    setSelectedMetadatos([]);
    setMetadatosFiltrados([]);
    if (value?.id) fetchMetadatos(value.id);
  };

  const buildHeaders = () => {
    const cols = metadatosFiltrados.filter((m) => selectedMetadatos.includes(m.id));
    return [
      ...FIXED_COLS,
      ...(incluirCamposCaja ? CAJA_COLS : []),
      ...cols.map((c) => `${selectedTipo.id}_${c.id}_${c.label_form}`),
    ];
  };

  const exportCSV = () => {
    const headers = buildHeaders();
    const blob = new Blob(
      ["\uFEFF" + headers.join(";") + "\n" + headers.map(() => "").join(";") + "\n"],
      { type: "text/csv;charset=utf-8;" }
    );
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `plantilla_${selectedTipo?.detalle || "contenido"}.csv`,
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportXLSX = async () => {
    const ExcelJS = require("exceljs");
    const headers = buildHeaders();
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Plantilla");

    const headerRow = ws.addRow(headers);
    headerRow.height = 22;
    headerRow.eachCell((cell, col) => {
      const isFixed = col <= FIXED_COLS.length;
      cell.font      = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: isFixed ? "FF37474F" : "FF1565C0" } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
    });

    ws.addRow(headers.map(() => ""));

    ws.columns.forEach((col, idx) => {
      col.width = Math.min(Math.max((headers[idx]?.length ?? 10) + 4, 18), 40);
    });

    ws.views = [{ state: "frozen", ySplit: 1 }];

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `plantilla_${selectedTipo?.detalle || "contenido"}.xlsx`,
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const selectedCols = metadatosFiltrados.filter((m) => selectedMetadatos.includes(m.id));
  const cajaCols = incluirCamposCaja ? CAJA_COLS.length : 0;
  const totalCols = FIXED_COLS.length + cajaCols + selectedCols.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 0.5 }}>

      {/* Paso 1: Tipo de contenido */}
      <Box>
        <StepLabel number={1} label="Elegí el tipo de contenido" />
        <Autocomplete
          options={tiposContenido}
          value={selectedTipo}
          onChange={handleTipoChange}
          getOptionLabel={(o) => `${o.id} — ${o.detalle}`}
          renderInput={(params) => (
            <TextField {...params} placeholder="Buscar tipo de contenido..." size="small" fullWidth />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id} sx={{ fontSize: "0.85rem" }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontFamily: "monospace" }}>
                {option.id}
              </Typography>
              {option.detalle}
            </Box>
          )}
        />
      </Box>

      {/* Paso 2: Metadatos */}
      {selectedTipo && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <StepLabel number={2} label="Seleccioná los campos de metadatos" />
            {!loading && metadatosFiltrados.length > 0 && (
              <Button size="small" onClick={toggleAll} sx={{ fontSize: "0.75rem" }}>
                {allSelected ? "Deseleccionar todo" : "Seleccionar todo"}
              </Button>
            )}
          </Box>

          {/* Columnas fijas (siempre incluidas) */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: "grey.50", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 1 }}>
              Columnas fijas (siempre incluidas)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {FIXED_COLS.map((col) => (
                <Chip
                  key={col}
                  label={col}
                  size="small"
                  sx={{ fontFamily: "monospace", fontSize: "0.7rem", bgcolor: "#37474F", color: "#fff" }}
                />
              ))}
            </Box>
          </Paper>

          {/* Columnas opcionales para crear cajas */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={incluirCamposCaja}
                  onChange={(e) => setIncluirCamposCaja(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" fontWeight={600}>
                  Incluir columnas para crear cajas
                </Typography>
              }
              sx={{ mb: incluirCamposCaja ? 1 : 0 }}
            />
            {incluirCamposCaja && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {CAJA_COLS.map((col) => (
                  <Chip
                    key={col}
                    label={col}
                    size="small"
                    sx={{ fontFamily: "monospace", fontSize: "0.7rem", bgcolor: "#546E7A", color: "#fff" }}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Cargando campos...</Typography>
            </Box>
          ) : metadatosFiltrados.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Este tipo de contenido no tiene metadatos configurados.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 280, overflowY: "auto", pr: 0.5 }}>
              {metadatosFiltrados.map((m) => {
                const selected = selectedMetadatos.includes(m.id);
                const badge = tipoBadge(m.tipo_dato);
                return (
                  <Box
                    key={m.id}
                    onClick={() => toggle(m.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      bgcolor: selected ? "primary.50" : "background.paper",
                      cursor: "pointer",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "primary.main", bgcolor: selected ? "primary.50" : "action.hover" },
                    }}
                  >
                    {selected
                      ? <CheckCircleIcon sx={{ fontSize: 18, color: "primary.main", flexShrink: 0 }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }} />
                    }
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={selected ? 600 : 400} noWrap>
                        {m.label_form}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {selectedTipo.id}_{m.id}_{m.label_form}
                      </Typography>
                    </Box>
                    <Chip
                      label={m.tipo_dato}
                      size="small"
                      sx={{ fontSize: "0.65rem", bgcolor: badge.bg, color: badge.text, flexShrink: 0 }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Paso 3: Descargar */}
      {selectedTipo && !loading && (
        <Box>
          <StepLabel number={3} label="Descargá la plantilla" />

          {/* Resumen de columnas */}
          {selectedMetadatos.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                La plantilla tendrá
              </Typography>
              <Chip label={`${totalCols} columnas`} size="small" color="primary" variant="outlined" />
              <Typography variant="caption" color="text.secondary">
                ({FIXED_COLS.length} fijas{cajaCols > 0 ? ` + ${cajaCols} de cajas` : ""} + {selectedCols.length} de metadatos)
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title="Recomendado · Encabezados con color, fila congelada" placement="top">
              <span style={{ flex: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<TableChartIcon />}
                  onClick={exportXLSX}
                  disabled={selectedMetadatos.length === 0}
                  sx={{ py: 1.2 }}
                >
                  Descargar XLSX
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Separado por punto y coma (;) con BOM UTF-8" placement="top">
              <span style={{ flex: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<DescriptionIcon />}
                  onClick={exportCSV}
                  disabled={selectedMetadatos.length === 0}
                  sx={{ py: 1.2 }}
                >
                  Descargar CSV
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

function StepLabel({ number, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box sx={{
        width: 22, height: 22, borderRadius: "50%",
        bgcolor: "primary.main", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "0.7rem", fontWeight: 700, flexShrink: 0,
      }}>
        {number}
      </Box>
      <Typography variant="body2" fontWeight={600}>{label}</Typography>
    </Box>
  );
}

export default ExportadorCSVTipoContenidos;
